import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import BuscaFactura from './BuscaFactura';
import Parametros from './Parametros';
import ReporteComprasVentas from './components/ReporteComprasVentas';
import TablaDetalles from './components/TablaDetalles';
import SubirExcelSicDetalleFactura from './components/SubirExcelSicDetalleFactura';
import SicDetalleFacturaSearch from './components/SicDetalleFacturaSearch';
import FacturasApp from './FacturasApp';
import SicInventario from './components/SicInventario';
import SicInOutInventario from './components/SicInOutInventario';
import Menu from './components/Menu';
import SicCotizaciones from './components/SicCotizaciones';
import Login from './components/Login';
import GestionUsuarios from './GestionUsuarios';
import GestionClientes from './components/GestionClientes';
import SicRecibos from './components/SicRecibos';
import GestionProveedores from './components/GestionProveedores';
import SicReporteGrupos from './components/SicReporteGrupos';
import './App.css';

function App() {
  const [vista, setVista] = useState('inicio');
  const [empresa, setEmpresa] = useState('');
  const [identificacion, setIdentificacion] = useState('');
  const [loadingEmpresa, setLoadingEmpresa] = useState(true);
  const [tipoReporte, setTipoReporte] = useState('comprasVentas');
  const [opcionesFacturas, setOpcionesFacturas] = useState('FacturasApp');
  
  // Estados para autenticación
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // Nuevo estado para el rol
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState(''); // Nuevo estado para errores de login

  // Estado para el tipo de cambio
  const [tipoCambio, setTipoCambio] = useState(null);
  const [loadingTipoCambio, setLoadingTipoCambio] = useState(false);

  // Verificar autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) setLoginError(''); // Limpiar error si el login es exitoso
      if (user) {
        // User is signed in, now get their role.
        try {
          const q = query(collection(db, 'usuarios'), where('email', '==', user.email));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            setUserRole(userData.rol);
          } else {
            setUserRole('user'); // Default to 'user' if no profile found
            console.warn(`No se encontró el perfil de usuario para ${user.email}. Asignando rol por defecto.`);
          }
        } catch (error) {
          console.error("Error al obtener el rol del usuario:", error);
          setUserRole('user'); // Default to 'user' on error
        }
      } else {
        setUserRole(null);
      }
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Cargar configuración solo si está autenticado
  useEffect(() => {
    if (user) {
      const cargarConfiguracion = async () => {
        try {
          const docRef = doc(db, 'Parametros', 'configuracion');
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            setEmpresa(data.empresa || '');
            setIdentificacion(data.identificacion || '');
          } else {
            setEmpresa('');
            setIdentificacion('');
          }
        } catch (error) {
          console.error('Error al cargar configuración:', error);
          setEmpresa('');
          setIdentificacion('');
        } finally {
          setLoadingEmpresa(false);
        }
      };

      cargarConfiguracion();
    }
  }, [user]);

  // Obtener tipo de cambio del dólar
  useEffect(() => {
    if (user) {
      const obtenerTipoCambio = async () => {
        setLoadingTipoCambio(true);
        try {
          const response = await fetch('https://api.hacienda.go.cr/indicadores/tc/dolar');
          // Si la respuesta no es exitosa (ej: 404, 500), lanza un error
          if (!response.ok) {
            throw new Error(`Error de red: ${response.status} ${response.statusText}`);
          }
          const data = await response.json();
          setTipoCambio(data);
        } catch (error) {
          console.error('Error al obtener tipo de cambio:', error);
          // Opcional: podrías setear un estado de error específico para el tipo de cambio
          setTipoCambio(null); // Asegurarse de que no se muestre un valor antiguo
        } finally {
          setLoadingTipoCambio(false);
        }
      };

      obtenerTipoCambio();
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Limpiar estados al cerrar sesión
      setVista('inicio');
      setEmpresa('');
      setIdentificacion('');
      setTipoCambio(null);
      setLoginError(''); // Limpiar error al cerrar sesión
      setUserRole(null); // Limpiar rol al cerrar sesión
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const renderContenido = () => {
    switch (vista) {
      case 'inicio':
        return null; // No renderiza nada por defecto
      case 'subir':
        return (
          <div>
            {opcionesFacturas === 'FacturasApp' && <FacturasApp />}
            {opcionesFacturas === 'BuscaFactura' && <BuscaFactura userEmail={user?.email || ''} />}
            {opcionesFacturas === 'BuscarDetalles' && (
              loadingEmpresa ? (
                <p>Cargando datos de configuración...</p>
              ) : identificacion ? (
                <TablaDetalles identificacion={identificacion} />
              ) : (
                <div className="configuracion-aviso">
                  <p>No se encontró la identificación de la empresa. Por favor, configure los parámetros.</p>
                  <button onClick={() => setVista('parametros')} className="btn-configurar">
                    ⚙️ Configurar Parámetros
                  </button>
                </div>
              )
            )}
          </div>
        );
      
      case 'reporte':
        return loadingEmpresa ? (
          <p>Cargando configuración...</p>
        ) : empresa ? (
          <div>
            {tipoReporte === 'comprasVentas' && (
              <ReporteComprasVentas empresa={empresa} identificacion={identificacion} />
            )}
            {tipoReporte === 'inventario' && (
              <SicInventario empresa={empresa} identificacion={identificacion} />
            )}
            {tipoReporte === 'estadoResultados' && <SicReporteGrupos />}
            {tipoReporte === 'balanceGeneral' && <div><h2>Balance General</h2><p>Aquí irá el reporte de Balance General</p></div>}
            {tipoReporte === 'flujoEfectivo' && <div><h2>Flujo de Efectivo</h2><p>Aquí irá el reporte de Flujo de Efectivo</p></div>}
          </div>
        ) : (
          <div className="configuracion-aviso">
            <p>No se encontró la configuración de la empresa</p>
            <button onClick={() => setVista('parametros')} className="btn-configurar">
              ⚙️ Configurar Parámetros
            </button>
          </div>
        );
      
      case 'parametros':
        return <Parametros />;
      
      case 'detalles': 
        return loadingEmpresa ? (
          <p>Cargando datos de configuración...</p>
        ) : identificacion ? (
          <TablaDetalles identificacion={identificacion} />
        ) : (
          <div className="configuracion-aviso">
            <p>No se encontró la identificación de la empresa. Por favor, configure los parámetros.</p>
            <button onClick={() => setVista('parametros')} className="btn-configurar">
              ⚙️ Configurar Parámetros
            </button>
          </div>
        );
      
      case 'importarArticulos':
        return <SicDetalleFacturaSearch />;
      
      case 'importarCatalogo':
        return <SubirExcelSicDetalleFactura />;

      case 'gestionUsuarios':
        if (userRole === 'admin') {
          return <GestionUsuarios />;
        } else {
          return <div className="configuracion-aviso" style={{backgroundColor: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb'}}>
            <p>🚫 Acceso Denegado. No tienes permisos para ver esta sección.</p>
          </div>;
        }

      case 'gestionClientes':
        return <GestionClientes />;

      case 'gestionProveedores':
        return <GestionProveedores />;

      case 'inOutInventario':
        return loadingEmpresa ? (
          <p>Cargando datos de configuración...</p>
        ) : identificacion ? (
          <SicInOutInventario empresa={empresa} identificacion={identificacion} />
        ) : (
          <div className="configuracion-aviso">
            <p>No se encontró la identificación de la empresa. Por favor, configure los parámetros.</p>
            <button onClick={() => setVista('parametros')} className="btn-configurar">
              ⚙️ Configurar Parámetros
            </button>
          </div>
        );

      case 'cotizaciones':
        return <SicCotizaciones />;

      case 'sicRecibos':
        return <SicRecibos/>;

      default:
        return <FacturasApp/>;
    }
  };

  // Mostrar loading mientras verifica autenticación
  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  // Si no está autenticado, mostrar login
  if (!user) {
    return <Login setLoginError={setLoginError} loginError={loginError} />;
  }

  // Formatear la fecha para mostrar
  // Se usa T12:00:00 para evitar que la conversión UTC→UTC-6 cambie el día
  const formatFecha = (fechaStr) => {
    try {
      const fecha = new Date(fechaStr + 'T12:00:00');
      return fecha.toLocaleDateString('es-CR', { timeZone: 'America/Costa_Rica' });
    } catch (error) {
      return fechaStr;
    }
  };

  // Si está autenticado, mostrar la aplicación normal
  return (
    <div className="app-windows">
      {/* Barra de Menú Principal */}
      <Menu 
        vista={vista}
        setVista={setVista}
        tipoReporte={tipoReporte}
        setTipoReporte={setTipoReporte}
        opcionesFacturas={opcionesFacturas}
        setOpcionesFacturas={setOpcionesFacturas}
        identificacion={identificacion}
        userRole={userRole}
      />
      {/* Barra de información de usuario y tipo de cambio */}
      <div className="app-header">
        <div className="user-info">
          {/* Mostrar tipo de cambio antes del user.email */}
          {loadingTipoCambio ? (
            <span className="tipo-cambio">💵 Cargando tipo de cambio...</span>
          ) : tipoCambio ? (
            <div className="tipos-cambio-container">
              <span className="tipo-cambio">
                💵 Venta: ₡{tipoCambio.venta?.valor ? tipoCambio.venta.valor.toFixed(2) : 'N/A'} 
              </span>
              <span className="tipo-cambio">
                💰 Compra: ₡{tipoCambio.compra?.valor ? tipoCambio.compra.valor.toFixed(2) : 'N/A'}
              </span>
              {tipoCambio.venta?.fecha && (
                <span className="fecha-cambio">
                  ({formatFecha(tipoCambio.venta.fecha)})
                </span>
              )}
            </div>
          ) : (
            <span className="tipo-cambio">💵 Error al cargar tipo de cambio</span>
          )}
          
          <span className="user-email">👤 {user.email}</span>
          <button onClick={handleLogout} className="logout-btn">
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>
      
      <div className="main-content">
        {renderContenido()}
      </div>
    </div>
  );
}

export default App;